/**
 * Composições do domínio da competição, sobre o design system.
 *
 * O DESIGN.md (seção 7) define como cada estado vindo do servidor deve
 * aparecer, e as regras não são cosméticas: sem verde para AC, sem amarelo
 * para fila, CE neutro para não parecer punição, e estado nunca dependendo só
 * de cor — sempre com ícone e texto acessível.
 */

import type { ContestStatus, SubmissionStatus, Verdict } from "@compila/shared";
import {
  Check,
  CircleAlert,
  Clock3,
  Code2,
  Hourglass,
  Loader,
  Lock,
  Pause,
  ServerCrash,
} from "lucide-react";
import { CONTEST_STATUS_LABEL, VERDICT_LABEL, formatClock } from "../lib/format.js";

/* ------------------------------------------------------------------ */
/* Veredito                                                            */
/* ------------------------------------------------------------------ */

type Aparencia = {
  Icone: typeof Check;
  rotulo: string;
  sigla: string | null;
  classe: string;
};

function aparenciaDe(status: SubmissionStatus, verdict: Verdict | null): Aparencia {
  const neutro = "border-border bg-background text-muted-foreground";
  const acento = "border-accent/40 bg-accent/10 text-accent";
  const falha = "border-destructive/40 bg-destructive/10 text-destructive";

  if (status === "QUEUED") {
    return { Icone: Hourglass, rotulo: "Na fila", sigla: null, classe: neutro };
  }
  if (status === "JUDGING") {
    return { Icone: Loader, rotulo: "Julgando", sigla: null, classe: acento };
  }
  if (status === "INTERNAL_ERROR") {
    // Falha de infraestrutura: não penaliza a equipe (regra 6 do CLAUDE.md).
    return { Icone: ServerCrash, rotulo: "Falha do juiz", sigla: null, classe: neutro };
  }
  if (!verdict) {
    return { Icone: Clock3, rotulo: "Sem veredito", sigla: null, classe: neutro };
  }
  if (verdict === "AC") {
    return { Icone: Check, rotulo: VERDICT_LABEL.AC, sigla: "AC", classe: acento };
  }
  if (verdict === "CE") {
    // Neutro de propósito: CE não conta como tentativa errada, e pintá-lo de
    // vermelho faria a equipe achar que perdeu pontos.
    return { Icone: Code2, rotulo: VERDICT_LABEL.CE, sigla: "CE", classe: neutro };
  }
  return {
    Icone: CircleAlert,
    rotulo: VERDICT_LABEL[verdict],
    sigla: verdict,
    classe: falha,
  };
}

export function VerdictBadge({
  status,
  verdict,
  size = "md",
}: {
  status: SubmissionStatus;
  verdict: Verdict | null;
  size?: "sm" | "md" | "lg";
}): React.JSX.Element {
  const { Icone, rotulo, sigla, classe } = aparenciaDe(status, verdict);
  const dimensoes = {
    sm: "gap-1 px-1.5 py-0.5 text-[11px]",
    md: "gap-1.5 px-2 py-1 text-xs",
    lg: "gap-2 px-3 py-1.5 text-sm",
  }[size];
  const icone = size === "lg" ? 16 : 13;

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${dimensoes} ${classe}`}
      // O texto completo acompanha a sigla: estado não pode depender de cor
      // nem de tooltip (DESIGN.md, seção 7).
      title={rotulo}
    >
      <Icone
        size={icone}
        className={`shrink-0 ${status === "JUDGING" ? "animate-spin" : ""}`}
        aria-hidden
      />
      {sigla ?? rotulo}
      {sigla && <span className="sr-only"> — {rotulo}</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Contest                                                             */
/* ------------------------------------------------------------------ */

export function ContestStatusBadge({
  status,
}: {
  status: ContestStatus;
}): React.JSX.Element {
  const emAndamento = status === "RUNNING";
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs">
      {status === "PAUSED" ? (
        <Pause size={12} className="text-muted-foreground" aria-hidden />
      ) : (
        <span
          className={`size-1.5 rounded-full ${emAndamento ? "bg-accent" : "bg-muted-foreground"}`}
          aria-hidden
        />
      )}
      {CONTEST_STATUS_LABEL[status]}
    </span>
  );
}

export function FrozenBanner({ big = false }: { big?: boolean }): React.JSX.Element {
  return (
    <div
      role="status"
      className={`inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 font-medium text-accent ${
        big ? "px-5 py-2.5 text-xl" : "px-3 py-1 text-xs"
      }`}
    >
      <Lock size={big ? 20 : 13} aria-hidden />
      Placar congelado
    </div>
  );
}

export function Countdown({
  elapsed,
  duration,
  status,
  size = "md",
}: {
  elapsed: number;
  duration: number;
  status: ContestStatus;
  size?: "md" | "lg";
}): React.JSX.Element {
  const restante = Math.max(0, duration - elapsed);
  // Últimos cinco minutos em destaque, com texto acessível — e sem piscar
  // (DESIGN.md, seção 7).
  const urgente = status === "RUNNING" && restante <= 300;

  return (
    <span
      className={`font-mono font-medium tabular-nums ${
        size === "lg" ? "text-5xl" : "text-xl"
      } ${urgente ? "text-destructive" : "text-foreground"} ${
        status === "PAUSED" ? "opacity-60" : ""
      }`}
    >
      {formatClock(restante)}
      <span className="sr-only">
        {urgente ? " restantes — menos de cinco minutos" : " restantes de prova"}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Estados de tela                                                     */
/* ------------------------------------------------------------------ */

export function Loading({ label = "Carregando…" }: { label?: string }) {
  return (
    <p role="status" className="py-16 text-center text-sm text-muted-foreground">
      {label}
    </p>
  );
}

export function EmptyState({
  icon: Icone,
  title,
  description,
  action,
}: {
  icon: typeof Check;
  title: string;
  description?: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-2 py-6 text-center">
      <Icone size={24} className="mx-auto text-muted-foreground" aria-hidden />
      <p className="text-sm font-medium">{title}</p>
      {description && (
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

/** Cabeçalho padrão das telas de administração. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-[28px] font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="mt-1 max-w-xl text-sm leading-6 text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions}
    </div>
  );
}

/** Bloco de código com rolagem — saída do compilador, stderr, código enviado. */
export function CodeBlock({
  children,
  label,
  tone = "neutral",
  maxHeight = "max-h-96",
}: {
  children: string;
  label?: string;
  tone?: "neutral" | "error";
  maxHeight?: string;
}): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      {label && (
        <div className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
          {label}
        </div>
      )}
      <pre className={`${maxHeight} overflow-auto p-4 text-xs leading-relaxed`}>
        <code className={tone === "error" ? "text-destructive" : "text-foreground"}>
          {children}
        </code>
      </pre>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tabelas                                                             */
/* ------------------------------------------------------------------ */

/**
 * Altura do viewport da tabela, para que poucas linhas nao produzam barra de
 * rolagem.
 *
 * O cabecalho e o corpo dividem o mesmo viewport rolavel, entao a altura tem
 * de cobrir `cabecalho + linhas`. Medido no navegador: a linha do cabecalho
 * renderiza a 64,5px com `rowHeight = 64`, porque a borda inferior dela nao
 * entra na altura declarada. Um unico pixel de excesso ja faz o `overflow-auto`
 * mostrar a barra — foi exatamente o que acontecia com 3 problemas numa tela
 * que cabia neles.
 *
 * A folga de 8px absorve essa borda e qualquer arredondamento de zoom ou fonte
 * sem abrir espaco visivel.
 */
export function tableHeight(
  rowCount: number,
  { rowHeight = 64, max = 520, minRows = 3 }: {
    rowHeight?: number;
    /** Acima disto a rolagem e legitima: a lista nao cabe mesmo. */
    max?: number;
    /** Piso, para o estado vazio nao virar uma faixa fina. */
    minRows?: number;
  } = {},
): number {
  const linhas = Math.max(rowCount, minRows);
  return Math.min(max, (linhas + 1) * rowHeight + 8);
}
